import { useParams } from 'react-router-dom';

import { useGetProjectDetailsQuery } from '../../api/adminProjectsApiSlice';
import { useEditProject } from '../../context/EditProjectContext';
import { Annotator, AllocationSummary, Concept, User } from '../../types/response';

interface UseProjectOverviewResults {
  projectName: string;
  conditionAreaName: string;
  healthSystemName: string;
  annotatorNames: string[];
  supervisorNames: string[];
  /** Persisted annotators/supervisors (id + label) — used by the editable variant. */
  annotators: User[];
  supervisors: User[];
  addedConcepts: Concept[];
  validationEncounterCount: number;
  testEncounterCount: number;
  trainingEncounterCount: number;
  isDataLoading: boolean;
  allocationSummary: AllocationSummary[];
}

export const useProjectOverview = (): UseProjectOverviewResults => {
  const { projectId } = useParams();
  const { addedAnnotators, removedSupervisorIds, confirmedReallocations } = useEditProject();
  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetProjectDetailsQuery(
    { projectId: projectId as string },
    { skip: !projectId || projectId === '' }
  );

  const annotators = projectDetailsData?.data?.annotators ?? [];
  const rawSupervisors = projectDetailsData?.data?.supervisors ?? [];
  const removedSupervisorSet = new Set(removedSupervisorIds);
  const supervisors = rawSupervisors.filter((s: User) => !removedSupervisorSet.has(s.id));
  const annotatorNames = annotators.map((annotator: Annotator) => annotator.label);
  const supervisorNames = supervisors.map((supervisor: User) => supervisor.label);
  const addedConcepts = projectDetailsData?.data?.conceptGroups.map((concept) => ({
    conceptGroupId: concept.conceptGroupId,
    conceptGroupName: concept.conceptGroupName,
    requiredAttributes: concept.requiredAttributes,
  })) as Concept[];

  // Overlay newly-added annotators into the allocation summary with zero
  // allocations so they appear immediately (UI-only until Save).
  const baseAllocation = projectDetailsData?.data?.allocationData ?? [];
  const existingAllocationIds = new Set(baseAllocation.map((row) => row.annotatorId));
  const addedAllocationRows: AllocationSummary[] = addedAnnotators
    .filter((annotator) => !existingAllocationIds.has(annotator.id))
    .map((annotator) => ({
      annotatorId: annotator.id,
      annotatorName: annotator.label,
      trainingCount: 0,
      testCount: 0,
      validationCount: 0,
    }));

  // Apply confirmed reallocation moves so the table reflects staged (unsaved) changes.
  const batchCountKey: Record<
    string,
    keyof Pick<AllocationSummary, 'trainingCount' | 'testCount' | 'validationCount'>
  > = {
    training: 'trainingCount',
    test: 'testCount',
    validation: 'validationCount',
  };
  const allocationById = new Map<string, AllocationSummary>(
    [...baseAllocation, ...addedAllocationRows].map((row) => [row.annotatorId, { ...row }])
  );
  confirmedReallocations.forEach((op) => {
    const key = batchCountKey[op.batch];
    const from = allocationById.get(op.fromAnnotatorId);
    const to = allocationById.get(op.toAnnotatorId);
    if (from) from[key] -= op.count;
    if (to) to[key] += op.count;
  });
  const allocationSummary = Array.from(allocationById.values());

  return {
    projectName: projectDetailsData?.data?.basics?.projectName ?? '',
    conditionAreaName: projectDetailsData?.data?.basics?.conditionAreaName ?? '',
    healthSystemName: projectDetailsData?.data?.basics?.healthSystemName ?? '',
    annotatorNames: annotatorNames ?? [],
    supervisorNames: supervisorNames ?? [],
    annotators,
    supervisors,
    addedConcepts: addedConcepts as Concept[],
    validationEncounterCount: projectDetailsData?.data?.dataSplitSummary?.validationCount ?? 0,
    testEncounterCount: projectDetailsData?.data?.dataSplitSummary?.testCount ?? 0,
    trainingEncounterCount: projectDetailsData?.data?.dataSplitSummary?.trainingCount ?? 0,
    isDataLoading: isProjectDetailsLoading || isProjectDetailsFetching,
    allocationSummary,
  };
};
